import { inject, injectable } from 'inversify'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import type { ClockGateway } from '@core/services/clock.gateway'
import { DEFAULT_MAX_AGE_MS, type FreshnessScope } from './freshness.config'

/**
 * When each `loadIfNeeded()` scope last completed a load, so returning to a
 * page that is still recent enough can reuse what is already in memory
 * instead of refetching — and a page left open for a long time still gets a
 * bound, instead of showing arbitrarily old data forever (P-56 in
 * the LogicRoom principle catalogue).
 *
 * A Repository-level stamp, not an operation policy: the async operation
 * core's state type is deliberately closed to this (spec §21 — "cache
 * freshness ... MUST NOT be preloaded into the core state type"), and no
 * timestamp exists anywhere in the operations system to build one from. This
 * sits next to the `isLoaded` boolean every `loadIfNeeded()` already checks,
 * narrowing it rather than replacing it.
 *
 * One Repository keyed by scope, not a field per feature Repository — the
 * same shape as `FiltersRepository`, and user-scoped for the same reason:
 * without `resetUserScopedState`, one user's "loaded 10s ago" would greet
 * the next login as if it still applied to them.
 */
@injectable()
export class FreshnessRepository implements UserScopedState {
  private readonly clockGateway: ClockGateway
  private loadedAtMsByScope: Partial<Record<FreshnessScope, number>> = {}

  constructor(@inject(Types.IClockGateway) clockGateway: ClockGateway) {
    this.clockGateway = clockGateway
  }

  markLoaded(scope: FreshnessScope): void {
    this.loadedAtMsByScope[scope] = this.clockGateway.now().getTime()
  }

  /** An unseen scope is never fresh — there is nothing to reuse yet. */
  isFresh(scope: FreshnessScope, maxAgeMs: number = DEFAULT_MAX_AGE_MS): boolean {
    const loadedAtMs = this.loadedAtMsByScope[scope]
    if (loadedAtMs === undefined) return false
    return this.clockGateway.now().getTime() - loadedAtMs < maxAgeMs
  }

  resetUserScopedState(): void {
    this.loadedAtMsByScope = {}
  }
}
